import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import toast from "react-hot-toast";
import { AccessToken } from "../enums/access-token";
import { ToastStatus } from "../enums/react-hot-toast";
import { PopUp } from "@/components/atoms/PopUp";

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_BACKEND_API,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(AccessToken.KEY);
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    if (response.data && !response.data.__typename) {
      const data = Object.values(response.data)[0];

      const { alert } = data;
      if (alert) {
        if (alert.type === ToastStatus.SUCCESS && alert?.message) {
          // TODO: fix this up just like with react query when switching fully to Apollo
          toast.custom(t => (
            <PopUp message={alert?.message} type="success" t={t} />
          ));
        } else if (alert.type === ToastStatus.ERROR && alert?.message) {
          toast.custom(t => (
            <PopUp message={alert?.message} type="error" t={t} />
          ));
        }
      }

      const payload = (data as any)?.payload;
      if (
        payload &&
        typeof payload === "object" &&
        payload !== null &&
        "token" in payload &&
        typeof (payload as any).token === "string"
      ) {
        localStorage.setItem(AccessToken.KEY, (payload as any).token);
      }
    }
    return response;
  });
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(error => {
      toast.custom(t => <PopUp message={error.message} type="error" t={t} />);
    });
  }

  if (networkError) {
    toast.custom(t => (
      <PopUp message={networkError.message} type="error" t={t} />
    ));
  }

  const isUnauthorizedError = graphQLErrors?.some(
    error =>
      error.message.toLowerCase().includes("unauthorized") ||
      error.message.toLowerCase().includes("token expired")
  );

  if (isUnauthorizedError) {
    localStorage.removeItem(AccessToken.KEY);
    window.location.href = "/signin";
  }
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, responseLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});
